import { ImageRatio } from '@diritto/common/base/constants';
import { ContentStatus } from '@diritto/common/content/constants';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { ModalType } from 'constants/modal';
import { ZIndex } from 'constants/zIndex';
import { MouseEvent, ReactNode, useCallback } from 'react';
import { getCoverImageBorder, getCoverImageStyle } from 'styles/common';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { ModalHandler } from 'utils/ModalHandler';

interface OldContentCoverProps {
  className?: string;
  content: ContentDto;
  canEnlarge?: boolean;
}

const CoverSize = {
  PC: 150,
  Mobile: 90,
};

// todo @deprecated
export const OldContentCover = ({ className, content, canEnlarge = false }: OldContentCoverProps) => {
  const handleClickCover = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!canEnlarge) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      ModalHandler.show(ModalType.ContentCover, {
        imageFile: content.cover,
      });
    },
    [content.cover, canEnlarge],
  );

  return (
    <StyledOldCoverWrapper className={className} canEnlarge={canEnlarge} onClick={handleClickCover}>
      <IkImage
        transform={{
          path: content.cover.path,
          isPrivate: content.cover.isPrivate,
          transformation: ImageTransformOptions[ImageTransformType.ContentCover],
        }}
        alt={`${content.title} 표지`}
      />
    </StyledOldCoverWrapper>
  );
};

const StyledOldCoverWrapper = styled.div(
  ({ canEnlarge }: { canEnlarge: boolean }) => css`
    ${getCoverImageBorder()};
    position: relative;
    width: ${CoverSize.Mobile}px;
    height: ${CoverSize.Mobile * ImageRatio}px;
    background-color: ${Color.White};

    cursor: ${canEnlarge ? 'pointer' : 'inherit'};

    & img {
      ${getCoverImageStyle()}
    }

    @media (${isPC}) {
      width: ${CoverSize.PC}px;
      height: ${CoverSize.PC * ImageRatio}px;
    }
  `,
);

interface ContentCoverProps {
  className?: string;
  content: ContentPublicRelayDto;
  canEnlarge?: boolean;
  size?: number;
  pcSize?: number;
  imageSize?: number;
  ratio?: number;
  children?: ReactNode;
}

export const ContentCover = ({
  className,
  content,
  canEnlarge = false,
  size = 100,
  pcSize,
  imageSize,
  ratio = ImageRatio,
  children,
}: ContentCoverProps) => {
  const isPrivate = content.status === ContentStatus.Private;

  const handleClickCover = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!canEnlarge) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      ModalHandler.show(ModalType.ContentCover, { imageFile: content.cover });
    },
    [content.cover, canEnlarge],
  );

  return (
    <StyledCoverWrapper
      className={className}
      canEnlarge={canEnlarge}
      ratio={ratio}
      size={size}
      pcSize={pcSize ?? size}
      onClick={handleClickCover}>
      <IkImage
        transform={{
          path: content.cover.path,
          isPrivate: content.cover.isPrivate,
          transformation: imageSize
            ? { ...ImageTransformOptions[ImageTransformType.ContentCover], width: imageSize }
            : ImageTransformOptions[ImageTransformType.ContentCover],
        }}
        alt={`${content.title} 표지`}
      />
      {isPrivate && <StyledPrivateCover />}
      {children}
    </StyledCoverWrapper>
  );
};

const StyledCoverWrapper = styled.div(
  ({ canEnlarge, size, pcSize, ratio }: { size: number; pcSize: number; canEnlarge: boolean; ratio: number }) => css`
    ${getCoverImageBorder()};
    position: relative;
    z-index: ${ZIndex.ForStackingContext};
    width: ${size}px;
    height: ${size * ratio}px;
    background-color: ${Color.White};

    cursor: ${canEnlarge ? 'pointer' : 'inherit'};

    & img {
      ${getCoverImageStyle()}
    }

    @media (${isPC}) {
      width: ${pcSize}px;
      height: ${pcSize * ratio}px;
    }
  `,
);

const StyledPrivateCover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: rgba(255, 255, 255, 0.5);
  z-index: ${ZIndex.CoverPrivateContentOpacity};
`;
